import {
  BALL_R,
  BRICK_FILL,
  FIELD,
  HUD_H,
  POWER_COLOR,
  POWER_LABEL,
  SHIELD_Y,
  WALL,
} from './constants';
import type { Ball, Brick, HudSnapshot, LaserShot, Paddle, Particle, Pickup, PowerUpId, Rotator } from './types';
import { drawParticles } from './particles';

export interface WorldView {
  bricks: Brick[];
  balls: Ball[];
  paddle: Paddle;
  pickups: Pickup[];
  lasers: LaserShot[];
  rotators: Rotator[];
  particles: Particle[];
  shield: boolean;
  shake: number;
  hud: HudSnapshot;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function brickColor(b: Brick): string {
  if (b.type === 'i') return BRICK_FILL.i;
  if (b.hp >= b.maxHp) return BRICK_FILL[b.type];
  if (b.hp === 1 && b.maxHp >= 2) return '#9ad4ff';
  if (b.hp === 2) return '#c9a5ff';
  return BRICK_FILL[b.type];
}

export function drawWorld(ctx: CanvasRenderingContext2D, world: WorldView, t: number): void {
  const sx = (Math.random() - 0.5) * world.shake;
  const sy = (Math.random() - 0.5) * world.shake;
  ctx.save();
  ctx.translate(sx, sy);

  const g = ctx.createLinearGradient(0, 0, 0, FIELD.h);
  g.addColorStop(0, '#070a18');
  g.addColorStop(1, '#10183a');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, FIELD.w, FIELD.h);

  ctx.fillStyle = 'rgba(19,244,255,0.04)';
  for (let x = 40; x < FIELD.w; x += 48) ctx.fillRect(x, HUD_H, 1, FIELD.h - HUD_H);
  for (let y = HUD_H + 24; y < FIELD.h; y += 48) ctx.fillRect(0, y, FIELD.w, 1);

  ctx.fillStyle = '#13f4ff';
  ctx.globalAlpha = 0.35;
  ctx.fillRect(0, HUD_H, FIELD.w, 2);
  ctx.fillRect(0, HUD_H, WALL, FIELD.h - HUD_H);
  ctx.fillRect(FIELD.w - WALL, HUD_H, WALL, FIELD.h - HUD_H);
  ctx.globalAlpha = 1;

  for (const b of world.bricks) {
    if (!b.alive) continue;
    ctx.save();
    ctx.shadowColor = brickColor(b);
    ctx.shadowBlur = b.type === 'i' ? 0 : 12;
    ctx.fillStyle = brickColor(b);
    roundRect(ctx, b.x, b.y, b.w, b.h, 4);
    ctx.fill();
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255,255,255,0.18)';
    roundRect(ctx, b.x + 2, b.y + 2, b.w - 4, 5, 2);
    ctx.fill();
    if (b.type === 'i') {
      ctx.strokeStyle = 'rgba(255,255,255,0.25)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(b.x + 6, b.y + 6);
      ctx.lineTo(b.x + b.w - 6, b.y + b.h - 6);
      ctx.moveTo(b.x + b.w - 6, b.y + 6);
      ctx.lineTo(b.x + 6, b.y + b.h - 6);
      ctx.stroke();
    }
    if (b.type === 'e') {
      ctx.fillStyle = '#fff3c4';
      ctx.beginPath();
      ctx.arc(b.x + b.w / 2, b.y + b.h / 2, 4, 0, Math.PI * 2);
      ctx.fill();
    }
    if (b.maxHp > 1 && b.type !== 'i') {
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.font = 'bold 10px Rajdhani, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(b.hp), b.x + b.w / 2, b.y + b.h / 2 + 1);
    }
    ctx.restore();
  }

  for (const rot of world.rotators) {
    ctx.save();
    ctx.translate(rot.x, rot.y);
    ctx.rotate(rot.angle);
    ctx.fillStyle = '#6b7898';
    ctx.shadowColor = '#9aa8c8';
    ctx.shadowBlur = 10;
    roundRect(ctx, -rot.length / 2, -rot.thickness / 2, rot.length, rot.thickness, 4);
    ctx.fill();
    ctx.beginPath();
    ctx.fillStyle = '#d7def0';
    ctx.arc(0, 0, 6, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  if (world.shield) {
    ctx.fillStyle = 'rgba(110,168,255,0.75)';
    ctx.shadowColor = '#6ea8ff';
    ctx.shadowBlur = 16;
    ctx.fillRect(WALL, SHIELD_Y, FIELD.w - WALL * 2, 5);
    ctx.shadowBlur = 0;
  }

  const paddle = world.paddle;
  const glow = glowColor(world.hud);
  ctx.save();
  ctx.shadowColor = glow;
  ctx.shadowBlur = 18;
  const pg = ctx.createLinearGradient(paddle.x, paddle.y, paddle.x + paddle.w, paddle.y);
  pg.addColorStop(0, '#13f4ff');
  pg.addColorStop(0.5, '#ffffff');
  pg.addColorStop(1, '#ff36d1');
  ctx.fillStyle = pg;
  roundRect(ctx, paddle.x, paddle.y, paddle.w, paddle.h, 7);
  ctx.fill();
  if (world.hud.activePowers.some((p) => p.id === 'laser')) {
    ctx.fillStyle = '#ff3d6e';
    ctx.fillRect(paddle.x + 8, paddle.y - 6, 5, 8);
    ctx.fillRect(paddle.x + paddle.w - 13, paddle.y - 6, 5, 8);
  }
  ctx.restore();

  for (const p of world.pickups) drawPickup(ctx, p, t);
  for (const shot of world.lasers) {
    ctx.fillStyle = '#ff3d6e';
    ctx.shadowColor = '#ff3d6e';
    ctx.shadowBlur = 10;
    ctx.fillRect(shot.x, shot.y, shot.w, shot.h);
    ctx.shadowBlur = 0;
  }

  for (const ball of world.balls) {
    for (let i = 0; i < ball.trail.length; i++) {
      const tr = ball.trail[i];
      ctx.globalAlpha = (i + 1) / ball.trail.length * 0.35;
      ctx.fillStyle = ball.fire ? '#ff4d2e' : '#13f4ff';
      ctx.beginPath();
      ctx.arc(tr.x, tr.y, BALL_R * (0.4 + i / ball.trail.length * 0.5), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.shadowColor = ball.fire ? '#ff4d2e' : ball.piercing ? '#9dfff0' : '#13f4ff';
    ctx.shadowBlur = 16;
    ctx.fillStyle = ball.fire ? '#ffb199' : '#ffffff';
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  drawParticles(ctx, world.particles);
  ctx.restore();
}

function glowColor(hud: HudSnapshot): string {
  const fire = hud.activePowers.find((p) => p.id === 'fireBall');
  if (fire) return POWER_COLOR.fireBall;
  const mag = hud.activePowers.find((p) => p.id === 'magnet');
  if (mag) return POWER_COLOR.magnet;
  const wide = hud.activePowers.find((p) => p.id === 'bigPaddle');
  if (wide) return POWER_COLOR.bigPaddle;
  return '#13f4ff';
}

function drawPickup(ctx: CanvasRenderingContext2D, p: Pickup, t: number): void {
  const bob = Math.sin(t * 6 + p.x) * 2;
  ctx.save();
  ctx.translate(p.x + p.w / 2, p.y + p.h / 2 + bob);
  ctx.fillStyle = POWER_COLOR[p.id];
  ctx.shadowColor = POWER_COLOR[p.id];
  ctx.shadowBlur = 12;
  roundRect(ctx, -14, -14, 28, 28, 6);
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.fillStyle = '#08101c';
  ctx.font = 'bold 11px Rajdhani, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(iconFor(p.id), 0, 1);
  ctx.restore();
}

function iconFor(id: PowerUpId): string {
  return POWER_LABEL[id].slice(0, 2).toUpperCase();
}
